import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  of,
  firstValueFrom,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  catchError,
  finalize,
  tap,
} from 'rxjs/operators';

import { TuiAlertService, TuiHostedDropdownComponent } from '@taiga-ui/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ClientService } from 'src/app/services/client/client.service';
import { BusinessUser } from 'src/app/models/client/client.model';
import { FormBuilder, FormGroup } from '@angular/forms';

import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { ModalAddClientComponent } from '../modal-add-client/modal-add-client.component';
import { pencilOutlineIcon, plusBlackIcon } from 'src/app/utils/icons-path';
import { Zone, ZonesResponse } from 'src/app/models/zone/zone.model';
import { ServicesService } from 'src/app/services/service/services.service';
import { ClientPlanTypeEnum } from 'src/app/enum/client-plan-type.enum';

type SortDirection = 'asc' | 'desc';

interface TableState {
  pageIndex: number; 
  pageSize: number;
  sortColumn: keyof BusinessUser | null;
  sortDirection: SortDirection;
  search: string;
  zoneId: string;
}

interface PaginatedClientsResponse {
  clientsData: BusinessUser[];
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

type ServerPage = {
  page: BusinessUser[];
  total: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

@Component({
  selector: 'app-clients-table',
  templateUrl: './clients-table.component.html',
  styleUrls: ['./clients-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientsTableComponent implements OnInit, OnDestroy {
  @ViewChild(TuiHostedDropdownComponent)
  private dropdown!: TuiHostedDropdownComponent;

  readonly sizes = ['l', 'm', 's'] as const;
  public Math = Math;
  readonly columns: Array<keyof BusinessUser | 'prices' | 'actions'> = [
    'businessName',
    'zoneName',
    'userId',
    'isEnabled',
    'clientPlan',
    'email',
    'phoneNumber',
    'contactName',
    'actions',
  ];
  private readonly DEFAULT_PAGE_SIZE = 10;

  private get exportableColumns(): Array<keyof BusinessUser> {
    return this.columns.filter(
      (c) => c !== 'actions' && c !== 'prices'
    ) as Array<keyof BusinessUser>;
  }

  private readonly headerKeyMap: Record<keyof BusinessUser, string> = {
    businessName: 'comercial.clients.columnBusinessName',
    zoneName: 'comercial.clients.zoneName',
    userId: 'comercial.clients.columnUserId',
    email: 'comercial.clients.columnEmail',
    contactName: 'comercial.clients.contactName',
    phoneNumber: 'comercial.clients.columnPhone',
    isEnabled: 'comercial.clients.columnStatus',
    clientPlan: 'comercial.clients.columnClientPlan',
    zoneGuid: 'comercial.clients.zoneGuid',
  };

  CLIENT_PLAN_I18N_KEY: Record<ClientPlanTypeEnum, string> = {
    [ClientPlanTypeEnum.Hybrid]: 'common.plan_type.plan_hybrid',
    [ClientPlanTypeEnum.Premium]: 'common.plan_type.plan_premium',
    [ClientPlanTypeEnum.Custom]: 'common.plan_type.plan_custom',
    [ClientPlanTypeEnum.B2b]: 'common.plan_type.plan_b2b',
    [ClientPlanTypeEnum.B2c]: 'common.plan_type.plan_b2c',
  };

  private static readonly UNASSIGNED_ZONE =
    '00000000-0000-0000-0000-000000000000';

  size = this.sizes[1];
  pencilOutline = pencilOutlineIcon;
  plusBlack = plusBlackIcon;

  filterForm!: FormGroup;
  exportOptions!: FormGroup;

  exportLoading = false;

  private readonly destroy$ = new Subject<void>();

  public readonly state$ = new BehaviorSubject<TableState>({
    pageIndex: 0,
    pageSize: this.DEFAULT_PAGE_SIZE,
    sortColumn: null,
    sortDirection: 'asc',
    search: '',
    zoneId: '',
  });

  private readonly serverPage$ = new BehaviorSubject<ServerPage>({
    page: [],
    total: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  private readonly serverRawPage$ = new BehaviorSubject<BusinessUser[]>([]);

  readonly view$ = this.serverPage$.asObservable();
  readonly totalFiltered$ = this.view$.pipe(map((v) => v.total));
  readonly pages$ = combineLatest([this.totalFiltered$, this.state$]).pipe(
    map(([total, st]) => Math.max(1, Math.ceil(total / st.pageSize)))
  );

  readonly rangeFrom$ = combineLatest([this.state$, this.view$]).pipe(
    map(([st, view]) => {
      const from = st.pageIndex * st.pageSize + (view.page.length > 0 ? 1 : 0);
      return from;
    })
  );

  readonly rangeTo$ = combineLatest([this.state$, this.view$]).pipe(
    map(([st, view]) => Math.min((st.pageIndex + 1) * st.pageSize, view.total))
  );

  readonly isLoading$ = new BehaviorSubject<boolean>(true);

  private readonly isFilteringSubject$ = new BehaviorSubject<boolean>(false);
  readonly isFiltering$ = this.isFilteringSubject$.asObservable();

  loadingClientId: string | null = null;
  isDropdownOpen = false;
  isLoadingDetails = false;
  openMenuId: string | null = null;
  zones: Zone[] = [];

  private lastEmittedState: TableState = this.state$.value;

  constructor(
    private readonly clientSvc: ClientService,
    private readonly offcanvas: NgbOffcanvas,
    private readonly alert: TuiAlertService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly servicesSvc: ServicesService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initForms();
    this.loadZones();
    this.bindStateToRemote();

    this.clientSvc.clientCreated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.patchState({ pageIndex: 0 });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForms(): void {
    this.exportOptions = this.fb.group({
      xls: false,
      csv: false,
    });

    this.filterForm = this.fb.group({
      search: [{ value: '', disabled: false }],
      zoneId: [{ value: null, disabled: false }],
    });

    this.filterForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ search, zoneId }) =>
        this.patchState({
          search: (search ?? '').trim(),
          zoneId: zoneId ?? '',
          pageIndex: 0,
        })
      );
  }

  private bindStateToRemote(): void {
    this.state$
      .pipe(
        debounceTime(200),
        distinctUntilChanged((a, b) => this.stateEquals(a, b)),
        tap((state) => {
          const isFilterChange =
            state.search !== this.lastEmittedState.search ||
            state.zoneId !== this.lastEmittedState.zoneId;

          this.isFilteringSubject$.next(isFilterChange);
          this.isLoading$.next(true);

          this.lastEmittedState = { ...state };
        }),
        switchMap((state) => {
          const payload = this.buildPayload(state);
          return this.clientSvc
            .getPaginatedClients(
              payload.pageNumber,
              payload.pageSize,
              payload.zoneGuid,
              payload.clientFilter
            )
            .pipe(
              catchError((err) => {
                this.handleError(err, 'comercial.clients.error_loading');
                return of<PaginatedClientsResponse>({
                  clientsData: [],
                  totalItems: 0,
                  hasNextPage: false,
                  hasPreviousPage: false,
                });
              }),
              finalize(() => {
                this.isLoading$.next(false);
                this.isFilteringSubject$.next(false);
                this.cdr.markForCheck();
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.processServerResponse(res);
      });
  }

  private buildPayload(state: TableState) {
    return {
      pageNumber: state.pageIndex + 1,
      pageSize: this.DEFAULT_PAGE_SIZE,
      zoneGuid:
        state.zoneId && state.zoneId !== ClientsTableComponent.UNASSIGNED_ZONE
          ? state.zoneId
          : '',
      clientFilter: state.search ? state.search : '',
    };
  }

  private processServerResponse(response: PaginatedClientsResponse): void {
    const page = Array.isArray(response.clientsData)
      ? response.clientsData
      : [];
    const total =
      typeof response.totalItems === 'number'
        ? response.totalItems
        : page.length;
    const hasNext = !!response.hasNextPage;
    const hasPrev = !!response.hasPreviousPage;

    if (this.ensurePageInRange(total)) {
      return;
    }

    this.serverRawPage$.next(page.slice());

    this.serverPage$.next({
      page: page.slice(),
      total,
      hasNextPage: hasNext,
      hasPreviousPage: hasPrev,
    });

    this.filterForm.get('search')?.enable({ emitEvent: false });
    this.filterForm.get('zoneId')?.enable({ emitEvent: false });
  }

  private ensurePageInRange(totalItems: number): boolean {
    const st = this.state$.value;
    const pages = Math.max(1, Math.ceil(totalItems / st.pageSize));
    if (st.pageIndex >= pages) {
      this.patchState({ pageIndex: Math.max(0, pages - 1) });
      return true;
    }
    return false;
  }

  private loadZones(): void {
    this.servicesSvc
      .getZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (z: ZonesResponse) => {
          this.zones = z.zones ?? [];
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.zones = [];
          this.handleError(err, 'common.error_generic');
        },
      });
  }

  trackByUserId(_: number, item: BusinessUser): string {
    return item.userId;
  }

  onSearch(term: string): void {
    this.filterForm.get('search')!.setValue(term);
  }

  onPageChange(pageIndex: number): void {
    const total = this.serverPage$.value.total;
    const pages = Math.max(1, Math.ceil(total / this.state$.value.pageSize));
    if (pageIndex < 0 || pageIndex >= pages) return;
    this.patchState({ pageIndex });
  }

  onSort(column: keyof BusinessUser): void {
    const st = this.state$.value;
    let newSortColumn: keyof BusinessUser | null = column;
    let newSortDirection: SortDirection = 'asc';

    if (st.sortColumn === column) {
      if (st.sortDirection === 'asc') {
        newSortDirection = 'desc';
      } else {
        newSortColumn = null;
        newSortDirection = 'asc';
      }
    }

    this.state$.next({
      ...this.state$.value,
      sortColumn: newSortColumn,
      sortDirection: newSortDirection,
    });

    const current = this.serverPage$.value;
    if (newSortColumn) {
      const ordered = [...current.page].sort((a, b) =>
        ClientsTableComponent.compare(
          a[newSortColumn as keyof BusinessUser],
          b[newSortColumn as keyof BusinessUser],
          newSortDirection
        )
      );
      this.serverPage$.next({ ...current, page: ordered });
      this.cdr.markForCheck();
    } else {
      const raw = this.serverRawPage$.value ?? [];
      this.serverPage$.next({ ...current, page: raw.slice() });
      this.cdr.markForCheck();
    }
  }

  openEditClient(client: BusinessUser): void {
    this.loadingClientId = client.userId;
    this.clientSvc.getClientDetails({ clientGuid: client.userId }).subscribe({
      next: (detail) => {
        const ref = this.offcanvas.open(ModalAddClientComponent, {
          backdrop: 'static',
          position: 'end',
        });
        ref.componentInstance.action = 'ed';
        ref.componentInstance.client = client;
        ref.componentInstance.clientDetails = detail;
        this.loadingClientId = null;
      },
      error: (err) => {
        this.loadingClientId = null;
        this.handleError(
          err,
          'comercial.clients.modal-add-client.error_loadingDetails'
        );
      },
    });
  }

  goToPrices(client: BusinessUser): void {
    localStorage.setItem('active_client_prices', JSON.stringify(client));
    this.router.navigate(['/prices'], {
      queryParams: { userId: client.userId },
    });
  }

  goToDetails(client: BusinessUser): void {
    this.router.navigate(['/comercial/clients/detail-client'], {
      queryParams: {
        userId: client.userId,
        clientName: client.businessName,
        zoneId: client.zoneGuid,
        zoneName: client.zoneName || '',
      },
    });
  }

  /**
   * Export: fetch all clients matching current filters from backend
   * Uses a local exportLoading indicator so it does NOT touch isLoading$ (table).
   */
  async export(): Promise<void> {
    const { xls, csv } = this.exportOptions.value;
    if (!xls && !csv) {
      this.alert
        .open(this.translate.instant('comercial.clients.selectFormatError'), {
          status: 'error',
        })
        .subscribe();
      return;
    }

    const raw = this.filterForm.getRawValue() as {
      search?: string | null;
      zoneId?: string | null;
    };

    const zoneGuid = raw.zoneId ?? '';
    const clientFilter = (raw.search ?? '').toString().trim();

    this.exportLoading = true;
    this.cdr.markForCheck();

    try {
  
      const allClients = await firstValueFrom(
        this.clientSvc.getAllClients({ zoneGuid: zoneGuid ?? '', clientFilter })
      );

      const dataToExport: BusinessUser[] = Array.isArray(allClients)
        ? allClients
        : [];

      if (!dataToExport.length) {
        this.alert
          .open(this.translate.instant('comercial.clients.noData'), {
            status: 'warning',
            autoClose: true,
          })
          .subscribe();
        return;
      }

      const headers = this.exportableColumns.map((col) =>
        this.translate.instant(this.headerKeyMap[col]!)
      );

      const rows = dataToExport.map((row) =>
        this.exportableColumns.map((col) => this.renderCell(col, row))
      );

      const base = `clients_${new Date().toISOString().slice(0, 10)}`;

      if (csv) this.downloadCSV(headers, rows, `${base}.csv`);
      if (xls) this.downloadXLS(headers, rows, `${base}.xlsx`);
    } catch (err) {
      console.error('[ClientsTable] export error', err);
      this.handleError(
        err,
        'comercial.clients.error_export' 
      );
    } finally {
      this.exportLoading = false;
      this.cdr.markForCheck();

      queueMicrotask(() => {
        this.exportOptions.reset(
          { xls: false, csv: false },
          { emitEvent: false }
        );
        this.isDropdownOpen = false;
        this.cdr.markForCheck();
      });
      this.isDropdownOpen = false;
    }
  }

  private renderCell(
    col: keyof BusinessUser,
    r: BusinessUser
  ): string | number {
    if (col === 'isEnabled') {
      return r.isEnabled
        ? this.translate.instant('comercial.clients.statusActive')
        : this.translate.instant('comercial.clients.statusInactive');
    }

    if (col === 'clientPlan') {
      const key = this.CLIENT_PLAN_I18N_KEY[
        r.clientPlan as ClientPlanTypeEnum
      ] as string;
      return this.translate.instant(key ?? '') || r.clientPlan;
    }

    return (r[col] ?? '') as string | number;
  }

  private downloadCSV(
    headers: string[],
    rows: (string | number)[][],
    fileName: string
  ): void {
    const csv = [headers, ...rows]
      .map((r) =>
        r.map((c) => `"${c.toString().replace(/"/g, '""')}"`).join(',')
      )
      .join('\r\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName);
  }

  private downloadXLS(
    headers: string[],
    rows: (string | number)[][],
    fileName: string
  ): void {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb: XLSX.WorkBook = { Sheets: { Data: ws }, SheetNames: ['Data'] };
    XLSX.writeFile(wb, fileName);
  }

  private handleError(error: unknown, i18nKey = 'common.error_generic'): void {
    console.error(error);
    this.alert
      .open(this.translate.instant(i18nKey), {
        status: 'error',
        autoClose: 3000,
      })
      .subscribe();
  }

  private patchState(patch: Partial<TableState>): void {
    const resetPage =
      patch.search !== undefined || patch.zoneId !== undefined
        ? { pageIndex: 0 }
        : {};
    this.state$.next({ ...this.state$.value, ...patch, ...resetPage });
  }

  private stateEquals(a: TableState, b: TableState): boolean {
    return (
      a.pageIndex === b.pageIndex &&
      a.pageSize === b.pageSize &&
      a.search === b.search &&
      a.zoneId === b.zoneId
    );
  }

  private static compare(
    aVal: unknown,
    bVal: unknown,
    direction: SortDirection
  ): number {
    const dir = direction === 'asc' ? 1 : -1;

    if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
      return aVal === bVal ? 0 : aVal ? -dir : dir;
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * dir;
    }

    return (
      String(aVal).localeCompare(String(bVal), undefined, {
        sensitivity: 'base',
        numeric: true,
      }) * dir
    );
  }

  @HostListener('document:mousedown', ['$event'])
  closeAllMenus(event: MouseEvent): void {
    const clickedInside = (event.target as HTMLElement).closest(
      '.dropdown-container'
    );
    if (!clickedInside) {
      this.openMenuId = null;
    }
  }

  toggleMenu(id: string): void {
    this.openMenuId = this.openMenuId === id ? null : id;
  }
}
